import type { NavGroup, NavLinkItem, NavCapabilityGate } from "../../nav/navConfig.js";
import { hasPermission } from "../permissions.js";

/** The account shape `hasPermission` accepts, without coupling to its name. */
type GateUser = Parameters<typeof hasPermission>[0];

/**
 * The context a nav tree is filtered against. Plain data plus two resolved
 * predicates, so a group's visibility is a pure function of its inputs: the
 * hook builds this once per render and the same decision powers the sidebar,
 * the command palette and the tests.
 */
export interface NavVisibilityContext {
  isAdmin: boolean;
  user: GateUser;
  installedApps: ReadonlySet<string>;
  /**
   * A SECOND presence source, unioned with `installedApps` for the
   * `requiresAnyApp` gate: tokens a provider reports as present box-wide even
   * when the viewer's own package set does not list them (e.g. a Media Requests
   * family `seerr`/`ombi` reachable through a remote-configured key, or installed
   * under another user). Empty when the caller cannot or did not resolve it —
   * the local-install path then governs on its own.
   */
  configuredRequestProviders: ReadonlySet<string>;
  /** System feature flags resolved to a single yes/no (e.g. `wsdashboard`). */
  isFeatureVisible: (feature?: string) => boolean;
  /** Async capability gates (AMC, runtime presence) resolved to a yes/no. */
  passesCapability: (gate: NavCapabilityGate | undefined) => boolean;
}

/** A nav entry survives when every gate it declares is satisfied. */
export const filterNavItem = (item: NavLinkItem, ctx: NavVisibilityContext): boolean => {
  if (item.requiresRole === "admin" && !ctx.isAdmin) return false;
  if (!ctx.isFeatureVisible(item.requiresFeature)) return false;
  if (item.requiresPermission && !hasPermission(ctx.user, item.requiresPermission)) return false;
  if (item.requiresApp && !ctx.installedApps.has(item.requiresApp)) return false;
  if (
    item.requiresAnyApp &&
    !item.requiresAnyApp.some((app) => ctx.installedApps.has(app) || ctx.configuredRequestProviders.has(app))
  )
    return false;
  if (!ctx.passesCapability(item.requiresCapability)) return false;
  return true;
};

/**
 * Filter a nav group and its subtree.
 *
 * A feature flag on a group is AUTHORITATIVE: when the system feature is off
 * the whole subtree is hidden regardless of any individually-grantable child,
 * so a child can never resurrect a feature-gated parent. Role and permission
 * gates stay non-authoritative below — the children decide whether the group
 * appears, so a grantee sees exactly the one entry they were given inside an
 * otherwise admin-only group, in its natural home, with no admin sibling.
 * (A landing page a granted user may not open is still withheld from the
 * header via `mayOpenGroupLanding`.)
 */
export const filterNavGroup = (group: NavGroup, ctx: NavVisibilityContext): NavGroup | null => {
  if (!ctx.isFeatureVisible(group.requiresFeature)) return null;

  const groupItems = group.items.filter((item) => filterNavItem(item, ctx));
  const childGroups = group.groups
    ?.map((child) => filterNavGroup(child, ctx))
    .filter((child): child is NavGroup => child !== null);
  if (!groupItems.length && (!childGroups || !childGroups.length)) return null;

  const mayOpenGroupLanding =
    !(group.requiresRole === "admin" && !ctx.isAdmin) &&
    ctx.isFeatureVisible(group.requiresFeature) &&
    !(group.requiresPermission && !hasPermission(ctx.user, group.requiresPermission)) &&
    ctx.passesCapability(group.requiresCapability);

  const { path: groupPath, ...rest } = group;
  return {
    ...rest,
    ...(mayOpenGroupLanding && groupPath ? { path: groupPath } : {}),
    items: groupItems,
    groups: childGroups?.length ? childGroups : undefined
  };
};
